import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { RoutineAssignment } from '../types/alarm';
import { routineAssignmentService } from '../services/routineAssignmentService';

interface RoutineAssignmentDetailPageProps {
  user: any;
  currentClass: any;
  routineId: string | null;
  onBack: () => void;
}

interface DateSummary {
  dateId: string;
  date: Date;
  dayOfWeek: string;
  isRoutineDay: boolean;
  status: {
    pendingCount: number;
    hasCompletedStudents: boolean;
    totalStudents: number;
  };
}

const RoutineAssignmentDetailPage: React.FC<RoutineAssignmentDetailPageProps> = ({
  user,
  currentClass,
  routineId,
  onBack
}) => {
  // routineId는 App.tsx에서 selectedRoutineId로 전달받음
  
  // 상태 관리
  const [routine, setRoutine] = useState<RoutineAssignment | null>(null);
  const [dateSummaries, setDateSummaries] = useState<DateSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 루틴 과제 데이터 로드
  useEffect(() => {
    const loadRoutineData = async () => {
      if (!routineId || !user?.id || !currentClass?.id) return;
      
      try {
        setIsLoading(true);
        
        // 1. 루틴 과제 정보 로드
        const routineData = await routineAssignmentService.loadRoutineAssignments(
          user.id,
          currentClass.id
        );
        const selectedRoutine = routineData.find(r => r.id === routineId);
        if (selectedRoutine) {
          setRoutine(selectedRoutine);
        }
        
        // 2. 클래스 멤버 로드
        // TODO: 멤버 데이터 로드 로직 구현 필요
        // 3. 날짜별 상태 요약 로드
        const allDateStatuses = await routineAssignmentService.loadRoutineAssignmentWithStatuses(
          user.id,
          currentClass.id,
          routineId
        );
        
        // 4. 날짜별 요약 정보 생성
        const summaries = processDateStatuses(
          allDateStatuses.dateStatuses,
          selectedRoutine?.routineDays
        );
        setDateSummaries(summaries);
        
      } catch (error) {
        console.error('루틴 과제 데이터 로드 실패:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadRoutineData();
  }, [routineId, user?.id, currentClass?.id]);
  
  // 날짜별 상태 요약 정보 생성
  const processDateStatuses = (dateStatuses: any[], routineDays?: number[]): DateSummary[] => {
    return dateStatuses.map(dateStatus => {
      const date = new Date(dateStatus.dateId);
      const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
      
      const students = dateStatus.students || {};
      const pendingCount = Object.values(students).filter(
        (s: any) => s.status === 'pending'
      ).length;
      
      const hasCompletedStudents = Object.values(students).some(
        (s: any) => s.status !== 'pending' || (s.earnedPoints && s.earnedPoints > 0)
      );
      
      const isRoutineDay = routineDays?.includes(date.getDay()) || false;
      
      return {
        dateId: dateStatus.dateId,
        date,
        dayOfWeek,
        isRoutineDay,
        status: {
          pendingCount,
          hasCompletedStudents,
          totalStudents: Object.keys(students).length
        }
      };
    }).sort((a, b) => a.date.getTime() - b.date.getTime()); // 날짜순 정렬
  };
  
  // 날짜별 블럭 클릭 핸들러
  const handleDateBlockClick = (dateId: string) => {
    console.log('날짜 블럭 클릭:', dateId);
  };
  
  // 뒤로가기 핸들러
  const handleBackClick = () => {
    onBack();
  };
  
  // 과제 설정 모달 열기
  const handleSettingsClick = () => {
    // TODO: 과제 설정 모달 열기
    console.log('과제 설정 모달 열기');
  };
  
  // 과제 체크 모달 열기 (전체)
  const handleCheckAllClick = () => {
    console.log('전체 과제 체크 클릭');
  };
  
  if (isLoading) {
    return (
      <Container>
        <LoadingText>로딩 중...</LoadingText>
      </Container>
    );
  }
  
  if (!routine) {
    return (
      <Container>
        <ErrorText>루틴 과제를 찾을 수 없습니다.</ErrorText>
        <BackButton onClick={handleBackClick}>뒤로가기</BackButton>
      </Container>
    );
  }
  
  return (
    <Container>
      {/* 헤더 */}
      <Header>
        <BackButton onClick={handleBackClick}>← 뒤로가기</BackButton>
        <Title>루틴 과제 관리</Title>
        <Subtitle>
          {routine.createdAt ? new Date(routine.createdAt).toLocaleDateString('ko-KR') : '날짜 없음'} - 
          {routine.categoryId || '분류 없음'}
        </Subtitle>
      </Header>
      
      {/* 선택된 루틴 과제 블럭 (최상단 고정) */}
      <SelectedRoutineBlock>
        <RoutineInfo>
          <RoutineTitle>{routine.title}</RoutineTitle>
          <RoutineDetails>
            <DetailItem>분류: {routine.categoryId || '없음'}</DetailItem>
            <DetailItem>요일: {[...routine.routineDays].sort((a, b) => a - b).map(day => ['일', '월', '화', '수', '목', '금', '토'][day]).join(', ')}</DetailItem>
            <DetailItem>기본 포인트: {routine.basePoints || 0}p</DetailItem>
          </RoutineDetails>
        </RoutineInfo>
        <ActionButtons>
          <ActionButton onClick={handleSettingsClick}>과제 설정</ActionButton>
          <ActionButton onClick={handleCheckAllClick}>과제 체크</ActionButton>
        </ActionButtons>
      </SelectedRoutineBlock>
      
      {/* 날짜별 과제 하위 블럭들 */}
      <DateBlocksSection>
        <SectionTitle>날짜별 과제 현황</SectionTitle>
        <DateBlocksGrid>
          {dateSummaries.map((dateSummary) => (
            <DateBlock
              key={dateSummary.dateId}
              onClick={() => handleDateBlockClick(dateSummary.dateId)}
            >
              <DateHeader>
                <DateText>{dateSummary.date.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}</DateText>
                <DayText>
                  ({dateSummary.dayOfWeek})
                  {dateSummary.isRoutineDay && <RoutineDayIcon>🌟</RoutineDayIcon>}
                </DayText>
              </DateHeader>
              <StatusText>
                {dateSummary.status.pendingCount > 0 
                  ? `미확인: ${dateSummary.status.pendingCount}명`
                  : dateSummary.status.hasCompletedStudents 
                    ? '과제 완료'
                    : '미확인: 0명'
                }
              </StatusText>
            </DateBlock>
          ))}
        </DateBlocksGrid>
      </DateBlocksSection>
      
      {/* 과제 체크 모달 - 임시로 제거 */}
      {/* TODO: AssignmentCheckModal 구현 후 추가 */}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 30px;
  text-align: center;
`;

const BackButton = styled.button`
  position: absolute;
  left: 20px;
  top: 20px;
  background: var(--color-surface-highlight);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: var(--color-border);
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  margin: 20px 0 10px 0;
  color: var(--color-text-primary);
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: var(--color-text-gray);
  margin: 0;
`;

const SelectedRoutineBlock = styled.div`
  background: var(--color-panel);
  border: 2px solid #007bff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RoutineInfo = styled.div`
  flex: 1;
`;

const RoutineTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 15px 0;
  color: var(--color-text-primary);
`;

const RoutineDetails = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const DetailItem = styled.span`
  font-size: 14px;
  color: var(--color-text-gray);
  background: var(--color-surface);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--color-border);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: #0056b3;
  }
`;

const DateBlocksSection = styled.div`
  margin-top: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 0;
  color: var(--color-text-primary);
`;

const DateBlocksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
`;

const DateBlock = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const DateHeader = styled.div`
  text-align: center;
  margin-bottom: 10px;
`;

const DateText = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: var(--color-text-primary);
  margin-bottom: 5px;
`;

const DayText = styled.div`
  font-size: 14px;
  color: var(--color-text-gray);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const RoutineDayIcon = styled.span`
  font-size: 16px;
`;

const StatusText = styled.div`
  text-align: center;
  font-size: 14px;
  color: var(--color-text-gray);
  padding: 8px;
  background: var(--color-panel);
  border-radius: 4px;
  border: 1px solid var(--color-border);
`;

const LoadingText = styled.div`
  text-align: center;
  font-size: 18px;
  color: var(--color-text-gray);
  margin-top: 100px;
`;

const ErrorText = styled.div`
  text-align: center;
  font-size: 18px;
  color: #dc3545;
  margin-top: 100px;
`;

export default RoutineAssignmentDetailPage; 